import React, { useState, useEffect, useMemo } from 'react';
import { X, Clock, User, Calendar, Building2, FileText, TrendingUp, Activity, ChevronDown, ChevronUp, Search, ArrowRight, Filter, Zap, AlertCircle } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import { useHistoryData } from '../hooks/useApi';
import { ParsedHistoryEntry, ApiData } from '../types/api';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  type: 'global' | 'cell';
  columnId?: number;
  industryId?: number;
  columnName?: string;
  industryName?: string;
  data?: ApiData;
}

interface GroupedEntry {
  key: string;
  industryName?: string;
  columnName?: string;
  entries: ParsedHistoryEntry[];
  latestValue: string;
  totalChanges: number;
  valueProgression: string[];
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  title,
  type,
  columnId,
  industryId,
  columnName,
  industryName,
  data,
}) => {
  const { loading, error, getGlobalHistory, getCellHistory } = useHistoryData();
  const [history, setHistory] = useState<ParsedHistoryEntry[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadHistory();
      setSearchTerm('');
      setFilterUser('');
      setExpandedGroups(new Set());
    }
  }, [isOpen, type, columnId, industryId]);

  const loadHistory = async () => {
    try {
      let historyData: ParsedHistoryEntry[] = [];
      
      if (type === 'global') {
        historyData = await getGlobalHistory(data);
      } else if (type === 'cell' && columnId && industryId) {
        historyData = await getCellHistory(columnId, industryId, data);
      }
      
      // Sort by date (newest first)
      historyData.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setHistory(historyData);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    }
  };

  const uniqueUsers = useMemo(() => {
    const users = new Set(history.map(entry => entry.user));
    return Array.from(users).sort();
  }, [history]);

  const filteredHistory = useMemo(() => {
    return history.filter(entry => {
      const matchesSearch = !searchTerm || 
        entry.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.industryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.columnName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesUser = !filterUser || entry.user === filterUser;
      
      return matchesSearch && matchesUser;
    });
  }, [history, searchTerm, filterUser]);

  const groupedHistory = useMemo(() => {
    if (type === 'cell') {
      // For cell history, group by industry
      const groups = new Map<string, GroupedEntry>();
      
      filteredHistory.forEach(entry => {
        const key = `industry-${entry.industryId}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            industryName: entry.industryName,
            columnName: entry.columnName,
            entries: [],
            latestValue: '',
            totalChanges: 0,
            valueProgression: [],
          });
        }
        
        const group = groups.get(key)!;
        group.entries.push(entry);
        group.totalChanges++;
      });
      
      // Calculate value progression for each group
      groups.forEach(group => {
        group.entries.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        group.valueProgression = group.entries.map(entry => entry.value);
        group.latestValue = group.entries[group.entries.length - 1]?.value || '';
        // Re-sort entries newest first for display
        group.entries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      });
      
      return Array.from(groups.values());
    } else {
      // For global history, group by industry + column combination
      const groups = new Map<string, GroupedEntry>();
      
      filteredHistory.forEach(entry => {
        const key = `${entry.industryId}-${entry.columnId}`;
        if (!groups.has(key)) {
          groups.set(key, {
            key,
            industryName: entry.industryName,
            columnName: entry.columnName,
            entries: [],
            latestValue: '',
            totalChanges: 0,
            valueProgression: [],
          });
        }
        
        const group = groups.get(key)!;
        group.entries.push(entry);
        group.totalChanges++;
      });
      
      // Calculate value progression for each group
      groups.forEach(group => {
        group.entries.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        group.valueProgression = group.entries.map(entry => entry.value);
        group.latestValue = group.entries[group.entries.length - 1]?.value || '';
        // Re-sort entries newest first for display
        group.entries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      });
      
      return Array.from(groups.values()).sort((a, b) => 
        new Date(b.entries[0].created_at).getTime() - new Date(a.entries[0].created_at).getTime()
      );
    }
  }, [filteredHistory, type]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      relative: getRelativeTime(date)
    };
  };

  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Há poucos minutos';
    if (diffInHours < 24) return `Há ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Há ${diffInDays} dia${diffInDays > 1 ? 's' : ''}`;
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `Há ${diffInWeeks} semana${diffInWeeks > 1 ? 's' : ''}`;
    
    return date.toLocaleDateString('pt-BR');
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getProgressColor = (index: number, total: number) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-red-500'
    ];
    return colors[index % colors.length];
  };

  const getChangeType = (oldValue: string, newValue: string) => {
    if (!oldValue && newValue) return { type: 'created', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', icon: '✨' };
    if (oldValue && !newValue) return { type: 'cleared', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: '🗑️' };
    if (oldValue !== newValue) return { type: 'modified', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: '✏️' };
    return { type: 'unchanged', color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', icon: '➖' };
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterUser('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{zIndex: 10000}}>
      <div className="bg-white rounded-2xl shadow-sm max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 "></div>
          <div className="relative flex items-center justify-between p-3 text-blue">
            <div className="flex items-center space-x-4 mb-20">
              <div className="p-3 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                <Activity className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{title}</h2>
                {type === 'cell' && columnName && industryName && (
                  <p className="text-blue-100 mt-1">
                    <span className="inline-flex items-center space-x-2">
                      <Building2 className="h-4 w-4" />
                      <span>{industryName}</span>
                      <span>•</span>
                      <FileText className="h-4 w-4" />
                      <span>{columnName}</span>
                    </span>
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="px-6 py-4 bg-gray-50 border-b space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por valor, indústria ou coluna..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="relative">
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os usuários</option>
                {uniqueUsers.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>
              
            </div>
            
            {(searchTerm || filterUser) && (
              <button
                onClick={clearFilters}
                className="px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpar
              </button>
            )}
          </div>
          
          {/* Stats Bar */}
          {!loading && !error && filteredHistory.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {filteredHistory.length} de {history.length} alteraç{filteredHistory.length === 1 ? 'ão' : 'ões'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Filter className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {groupedHistory.length} {type === 'cell' ? 'indústria' : 'item'}{groupedHistory.length === 1 ? '' : type === 'cell' ? 's' : 's'} exibida{groupedHistory.length === 1 ? '' : 's'}
                  </span>
                </div>
              </div>
              {filteredHistory.length > 0 && (
                <div className="text-sm text-gray-500">
                  Última alteração: {formatDate(filteredHistory[0]?.created_at).relative}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <LoadingSpinner size="lg" className="mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Carregando histórico...</p>
                <p className="text-gray-400 text-sm mt-1">Analisando alterações</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao Carregar</h3>
              <p className="text-gray-600">{error}</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                {searchTerm || filterUser ? (
                  <Search className="h-10 w-10 text-gray-400" />
                ) : (
                  <Clock className="h-10 w-10 text-gray-400" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {searchTerm || filterUser ? 'Nenhum Resultado Encontrado' : 'Nenhum Histórico Encontrado'}
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {searchTerm || filterUser 
                  ? 'Tente ajustar os filtros de busca para encontrar as alterações desejadas.'
                  : type === 'cell' 
                    ? 'Esta célula ainda não foi modificada. As alterações aparecerão aqui quando forem feitas.'
                    : 'Nenhuma alteração foi registrada ainda. O histórico será exibido conforme as modificações forem realizadas.'
                }
              </p>
              {(searchTerm || filterUser) && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Limpar Filtros
                </button>
              )}
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-6">
                {groupedHistory.map((group, groupIndex) => {
                  const isExpanded = expandedGroups.has(group.key);
                  const { date: latestDate, relative } = formatDate(group.entries[0].created_at);
                  
                  return (
                    <div key={group.key} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      {/* Group Header */}
                      <div 
                        className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleGroup(group.key)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className={`w-12 h-12 rounded-xl ${getProgressColor(groupIndex, groupedHistory.length)} flex items-center justify-center text-white font-bold text-lg shadow-sm`}>
                              {group.totalChanges}
                            </div>
                            <div>
                              <div className="flex items-center space-x-3">
                                {type === 'global' && (
                                  <>
                                    <div className="flex items-center space-x-1 text-gray-700">
                                      <Building2 className="h-4 w-4 text-blue-600" />
                                      <span className="font-semibold">{group.industryName || 'Indústria desconhecida'}</span>
                                    </div>
                                    <span className="text-gray-400">•</span>
                                    <div className="flex items-center space-x-1 text-gray-700">
                                      <FileText className="h-4 w-4 text-purple-600" />
                                      <span className="font-semibold">{group.columnName || 'Coluna desconhecida'}</span>
                                    </div>
                                  </>
                                )}
                                {type === 'cell' && (
                                  <div className="flex items-center space-x-1 text-gray-700">
                                    <Building2 className="h-4 w-4 text-blue-600" />
                                    <span className="font-semibold">{group.industryName || 'Indústria desconhecida'}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center space-x-4 mt-2">
                                <span className="text-sm text-gray-500">
                                  {group.totalChanges} alteraç{group.totalChanges === 1 ? 'ão' : 'ões'}
                                </span>
                                <span className="text-sm text-gray-400">•</span>
                                <span className="text-sm text-gray-500">{relative}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            {/* Value Progression Preview */}
                            {group.valueProgression.length > 1 && (
                              <div className="flex items-center space-x-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg px-4 py-2 border border-blue-200">
                                <div className="text-xs font-medium text-gray-600">Evolução:</div>
                                <div className="flex items-center space-x-1">
                                  <span className="text-xs bg-white px-2 py-1 rounded border text-gray-700 max-w-20 truncate">
                                    {group.valueProgression[0] || 'Vazio'}
                                  </span>
                                  <ArrowRight className="h-3 w-3 text-gray-400" />
                                  <span className="text-xs bg-white px-2 py-1 rounded border text-gray-700 max-w-20 truncate">
                                    {group.valueProgression[group.valueProgression.length - 1] || 'Vazio'}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">Valor Atual</div>
                              <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-lg mt-1 max-w-32 truncate">
                                {group.latestValue || <span className="italic text-gray-400">Vazio</span>}
                              </div>
                            </div>
                            <div className="flex items-center">
                              {isExpanded ? (
                                <ChevronUp className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Timeline */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 p-6 bg-gray-50">
                          <div className="relative">
                            {/* Timeline line */}
                            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 to-purple-400"></div>
                            
                            <div className="space-y-6">
                              {group.entries.map((entry, index) => {
                                const { date, time, relative } = formatDate(entry.created_at);
                                const isFirst = index === 0;
                                const isLast = index === group.entries.length - 1;
                                const previousEntry = group.entries[index + 1];
                                const changeInfo = getChangeType(previousEntry?.value || '', entry.value);
                                
                                return (
                                  <div key={index} className="relative flex items-start space-x-4">
                                    {/* Timeline dot */}
                                    <div className={`relative z-10 w-3 h-3 rounded-full ${
                                      isFirst ? 'bg-green-500' : isLast ? 'bg-gray-400' : 'bg-blue-500'
                                    } border-2 border-white shadow-sm`}></div>
                                    
                                    {/* Content */}
                                    <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center space-x-3">
                                          <div className="flex items-center space-x-2">
                                            <User className="h-4 w-4 text-gray-400" />
                                            <span className="font-medium text-gray-900">{entry.user}</span>
                                          </div>
                                          {isFirst && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                              <Zap className="h-3 w-3 mr-1" />
                                              Mais recente
                                            </span>
                                          )}
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${changeInfo.bg} ${changeInfo.color} border ${changeInfo.border}`}>
                                            <span className="mr-1">{changeInfo.icon}</span>
                                            {changeInfo.type === 'created' ? 'Criado' : 
                                             changeInfo.type === 'cleared' ? 'Limpo' : 
                                             changeInfo.type === 'modified' ? 'Alterado' : 'Inalterado'}
                                          </span>
                                        </div>
                                        <div className="text-right text-sm text-gray-500">
                                          <div className="flex items-center space-x-1">
                                            <Calendar className="h-3 w-3" />
                                            <span>{date}</span>
                                          </div>
                                          <div className="flex items-center space-x-1 mt-1">
                                            <Clock className="h-3 w-3" />
                                            <span>{time}</span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Value Comparison */}
                                      {!isLast && previousEntry && (
                                        <div className="mb-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3 border border-orange-200">
                                          <div className="flex items-center space-x-4">
                                            <div className="flex-1">
                                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Valor Anterior
                                              </label>
                                              <div className="bg-red-100 border border-red-200 rounded px-3 py-2 text-sm text-gray-900">
                                                {previousEntry.value || (
                                                  <span className="text-gray-400 italic">Vazio</span>
                                                )}
                                              </div>
                                            </div>
                                            
                                            <div className="flex-shrink-0">
                                              <ArrowRight className="h-5 w-5 text-orange-500" />
                                            </div>
                                            
                                            <div className="flex-1">
                                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                                Novo Valor
                                              </label>
                                              <div className="bg-green-100 border border-green-200 rounded px-3 py-2 text-sm text-gray-900">
                                                {entry.value || (
                                                  <span className="text-gray-400 italic">Vazio</span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Current Value (for first/last entries) */}
                                      {(isLast || isFirst) && (
                                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-3 border border-blue-100">
                                          <div className="text-center">
                                            <label className="block text-xs font-medium text-gray-600 mb-2">
                                              {isFirst ? 'Valor Atual' : 'Valor Inicial'}
                                            </label>
                                            <div className="bg-white border border-blue-200 rounded-lg px-4 py-2 text-sm font-medium text-gray-900">
                                              {entry.value || (
                                                <span className="text-gray-400 italic">Vazio</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {filteredHistory.length > 0 && (
              <span>
                Exibindo {groupedHistory.length} {type === 'cell' ? 'indústria' : 'item'}{groupedHistory.length === 1 ? '' : type === 'cell' ? 's' : 's'} com alterações
                {(searchTerm || filterUser) && ` (filtrado de ${history.length} total)`}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 font-medium shadow-sm"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};